import React from 'react';
import PropTypes from 'prop-types';

// Map weather codes to icon types
const getIconType = (weatherCode, isNight = false) => {
  // Clear
  if (weatherCode === 0) return isNight ? 'CLEAR_NIGHT' : 'CLEAR_DAY';
  
  // Partly cloudy
  if (weatherCode >= 1 && weatherCode <= 2) return isNight ? 'PARTLY_CLOUDY_NIGHT' : 'PARTLY_CLOUDY_DAY';
  
  // Cloudy/Overcast
  if (weatherCode === 3) return 'CLOUDY';
  
  // Fog
  if (weatherCode >= 45 && weatherCode <= 48) return 'FOG';
  
  // Drizzle
  if (weatherCode >= 51 && weatherCode <= 57) return 'LIGHT_RAIN';
  
  // Rain
  if (weatherCode >= 61 && weatherCode <= 67) {
    if (weatherCode >= 65) return 'HEAVY_RAIN';
    return 'RAIN';
  }
  
  // Snow
  if (weatherCode >= 71 && weatherCode <= 77) return 'SNOW';
  
  // Rain showers
  if (weatherCode >= 80 && weatherCode <= 82) return 'RAIN';
  
  // Snow showers
  if (weatherCode >= 85 && weatherCode <= 86) return 'SNOW';
  
  // Thunderstorm
  if (weatherCode >= 95 && weatherCode <= 99) return 'THUNDERSTORM';
  
  return 'CLEAR_DAY';
};

const AnimatedWeatherIcon = ({ weatherCode, isNight, size = 100, color = '#FFB347' }) => {
  const iconType = getIconType(weatherCode, isNight);
  
  const renderIcon = () => {
    switch (iconType) {
      case 'CLEAR_DAY':
        return (
          <g>
            <circle cx="50" cy="50" r="20" fill={color} className="sun-core">
              <animate attributeName="r" values="18;22;18" dur="2s" repeatCount="indefinite" />
            </circle>
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
              <line
                key={i}
                x1="50"
                y1="50"
                x2={50 + 35 * Math.cos((angle * Math.PI) / 180)}
                y2={50 + 35 * Math.sin((angle * Math.PI) / 180)}
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                className="sun-ray"
              >
                <animateTransform
                  attributeName="transform"
                  type="rotate"
                  from="0 50 50"
                  to="360 50 50"
                  dur="20s"
                  repeatCount="indefinite"
                />
              </line>
            ))}
          </g>
        );
        
      case 'CLEAR_NIGHT':
        return (
          <g>
            <path
              d="M 50 20 C 35 20, 25 35, 25 50 C 25 65, 35 80, 50 80 C 40 80, 30 65, 30 50 C 30 35, 40 20, 50 20"
              fill="#E6E6FA"
              className="moon"
            >
              <animateTransform
                attributeName="transform"
                type="rotate"
                from="0 50 50"
                to="10 50 50"
                dur="4s"
                repeatCount="indefinite"
                values="0 50 50; 10 50 50; 0 50 50"
              />
            </path>
            {/* Stars */}
            {[[30, 25], [70, 30], [65, 70], [35, 65]].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="1.5" fill="white">
                <animate
                  attributeName="opacity"
                  values="0;1;0"
                  dur={`${2 + i * 0.5}s`}
                  repeatCount="indefinite"
                />
              </circle>
            ))}
          </g>
        );
        
      case 'PARTLY_CLOUDY_DAY':
        return (
          <g>
            {/* Sun behind cloud */}
            <circle cx="35" cy="35" r="15" fill={color} opacity="0.8">
              <animate attributeName="r" values="13;17;13" dur="2s" repeatCount="indefinite" />
            </circle>
            {/* Cloud */}
            <g transform="translate(20, 35)">
              <ellipse cx="25" cy="25" rx="25" ry="12" fill="#D3D3D3" opacity="0.9">
                <animate attributeName="rx" values="25;27;25" dur="3s" repeatCount="indefinite" />
              </ellipse>
              <ellipse cx="15" cy="20" rx="15" ry="10" fill="#D3D3D3" opacity="0.9" />
              <ellipse cx="35" cy="20" rx="15" ry="10" fill="#D3D3D3" opacity="0.9" />
            </g>
          </g>
        );
        
      case 'CLOUDY':
        return (
          <g>
            <g transform="translate(0, 20)">
              <ellipse cx="50" cy="40" rx="30" ry="15" fill="#B0B0B0" opacity="0.9">
                <animate attributeName="rx" values="30;32;30" dur="4s" repeatCount="indefinite" />
              </ellipse>
              <ellipse cx="35" cy="35" rx="20" ry="13" fill="#B0B0B0" opacity="0.9" />
              <ellipse cx="65" cy="35" rx="20" ry="13" fill="#B0B0B0" opacity="0.9" />
            </g>
          </g>
        );
        
      case 'RAIN':
      case 'LIGHT_RAIN':
        return (
          <g>
            {/* Cloud */}
            <g transform="translate(0, 10)">
              <ellipse cx="50" cy="30" rx="30" ry="15" fill="#808080" opacity="0.9" />
              <ellipse cx="35" cy="25" rx="20" ry="13" fill="#808080" opacity="0.9" />
              <ellipse cx="65" cy="25" rx="20" ry="13" fill="#808080" opacity="0.9" />
            </g>
            {/* Rain drops */}
            {[25, 40, 50, 60, 75].map((x, i) => (
              <line
                key={i}
                x1={x}
                y1="50"
                x2={x - 2}
                y2="60"
                stroke="#4FC3F7"
                strokeWidth="2"
                opacity="0"
              >
                <animate
                  attributeName="opacity"
                  values="0;1;0"
                  dur="1s"
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                />
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0,0; 0,20; 0,40"
                  dur="1s"
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                />
              </line>
            ))}
          </g>
        );
        
      case 'THUNDERSTORM':
        return (
          <g>
            {/* Dark cloud */}
            <g transform="translate(0, 10)">
              <ellipse cx="50" cy="30" rx="30" ry="15" fill="#505050" opacity="0.9" />
              <ellipse cx="35" cy="25" rx="20" ry="13" fill="#505050" opacity="0.9" />
              <ellipse cx="65" cy="25" rx="20" ry="13" fill="#505050" opacity="0.9" />
            </g>
            {/* Lightning */}
            <path
              d="M 45 45 L 50 55 L 45 55 L 50 65 L 55 55 L 50 55 L 55 45 Z"
              fill="#FFD700"
              opacity="0"
            >
              <animate
                attributeName="opacity"
                values="0;0;1;0"
                dur="3s"
                repeatCount="indefinite"
              />
            </path>
            {/* Rain */}
            {[30, 50, 70].map((x, i) => (
              <line
                key={i}
                x1={x}
                y1="50"
                x2={x - 2}
                y2="60"
                stroke="#4FC3F7"
                strokeWidth="2"
                opacity="0"
              >
                <animate
                  attributeName="opacity"
                  values="0;1;0"
                  dur="0.8s"
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                />
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0,0; 0,20; 0,40"
                  dur="0.8s"
                  begin={`${i * 0.2}s`}
                  repeatCount="indefinite"
                />
              </line>
            ))}
          </g>
        );
        
      case 'SNOW':
        return (
          <g>
            {/* Cloud */}
            <g transform="translate(0, 10)">
              <ellipse cx="50" cy="30" rx="30" ry="15" fill="#C0C0C0" opacity="0.9" />
              <ellipse cx="35" cy="25" rx="20" ry="13" fill="#C0C0C0" opacity="0.9" />
              <ellipse cx="65" cy="25" rx="20" ry="13" fill="#C0C0C0" opacity="0.9" />
            </g>
            {/* Snowflakes */}
            {[25, 40, 50, 60, 75].map((x, i) => (
              <text
                key={i}
                x={x}
                y="50"
                fontSize="12"
                fill="white"
                opacity="0"
              >
                ❄
                <animate
                  attributeName="opacity"
                  values="0;1;0"
                  dur="2s"
                  begin={`${i * 0.3}s`}
                  repeatCount="indefinite"
                />
                <animateTransform
                  attributeName="transform"
                  type="translate"
                  values="0,0; -5,20; 0,40"
                  dur="2s"
                  begin={`${i * 0.3}s`}
                  repeatCount="indefinite"
                />
              </text>
            ))}
          </g>
        );
        
      case 'FOG':
        return (
          <g>
            {[20, 30, 40, 50, 60].map((y, i) => (
              <line
                key={i}
                x1="15"
                y1={y}
                x2="85"
                y2={y}
                stroke="#B0B0B0"
                strokeWidth="3"
                strokeLinecap="round"
                opacity="0.6"
              >
                <animate
                  attributeName="x1"
                  values="15;10;15"
                  dur={`${3 + i * 0.5}s`}
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="x2"
                  values="85;90;85"
                  dur={`${3 + i * 0.5}s`}
                  repeatCount="indefinite"
                />
              </line>
            ))}
          </g>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className="animated-weather-icon"
    >
      {renderIcon()}
    </svg>
  );
};

AnimatedWeatherIcon.propTypes = {
  weatherCode: PropTypes.number.isRequired,
  isNight: PropTypes.bool,
  size: PropTypes.number,
  color: PropTypes.string
};

export default AnimatedWeatherIcon;